/**
 * Snapshot of what the next stack may send right now.
 * Legacy pages set `stack: 'legacy'`, which leaves today's sinks unchanged.
 * The default is closed so a next-stack page cannot emit before the provider runs.
 */

export type ConsentGateState = {
  stack: 'next' | 'legacy';
  stats: boolean;
  analytics: boolean;
  ads: boolean;
  gtm: boolean;
  replay: boolean;
  /** False on private pages, and until attribution consent. */
  attribution: boolean;
};

const CLOSED: ConsentGateState = {
  stack: 'next',
  stats: false,
  analytics: false,
  ads: false,
  gtm: false,
  replay: false,
  attribution: false,
};

let gate: ConsentGateState = CLOSED;

export function setConsentGate(next: ConsentGateState): void {
  gate = next;
}

export function getConsentGate(): ConsentGateState {
  return gate;
}

export function allowsStats(): boolean {
  return gate.stack === 'legacy' || gate.stats;
}

/** Named behavioural events. Automatic pageviews use `allowsStats` instead. */
export function allowsAnalytics(): boolean {
  return gate.stack === 'legacy' || gate.analytics;
}

export function allowsDataLayer(): boolean {
  return gate.stack === 'legacy' || gate.gtm;
}

export function allowsAds(): boolean {
  return gate.stack === 'legacy' || gate.ads;
}

/**
 * Internal links must not copy ad click ids on the server render or the first
 * client render. The live gate can already be open from the consent cookie
 * before hydration finishes, and the href has to match the server HTML.
 */
let clickIdForwardingReady = false;

export function markClickIdForwardingReady(): void {
  clickIdForwardingReady = true;
}

export function clickIdsForwardable(): boolean {
  return clickIdForwardingReady && allowsAds();
}
